import { createApi, fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { config } from '../utils/config';
import { storeAccessToken, isAccessTokenExpired, isJwtExpiredResponse } from '../utils/accessToken';

interface TokenResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
}

interface PaymentIntent {
  id: string;
  created: number;
  status: string;
  amount: number;
  currency: string;
  country?: string;
  client_secret: string;
  metadata: {
    platform_customer_id: string;
    client_id: string;
  };
}
interface SetupIntent {
  created: number;
  id: string;
  metadata: {
    client_id?: string;
    [key: string]: string | undefined;
  };
  status: string;
  client_secret: string;
}
interface PaymentRequestResponse {
  publishable_key: string;
  connected_account_id: string;
  platform_account_id: string;
  payment_intent: PaymentIntent;
  setup_intent: SetupIntent;
}
export interface UpdatePaymentRequestRequest {
  pending_transaction_status: string;
  /**
   * Sent with pending_transaction_status: 'manual-confirm' when CVC is collected.
   * Produced by stripe.createConfirmationToken() from the Payment Element.
   */
  confirmation_token?: string;
  /**
   * Sent with pending_transaction_status: 'manual-confirm' when CVC is skipped.
   * Produced by stripe.createPaymentMethod() from the legacy card elements.
   */
  payment_method_id?: string;
}

/**
 * Response for a MOTO server-side confirmation. The server confirms the
 * SetupIntent and reports the resulting status so the client can run
 * stripe.handleNextAction() when 3DS is required.
 */
export interface UpdatePaymentRequestResponse {
  status?: string;
  client_secret?: string;
  setup_intent?: {
    id?: string;
    status?: string;
    client_secret?: string;
  };
  message?: string;
  error?: string;
}

const rawBaseQuery = fetchBaseQuery({
    baseUrl: config.api.authUrl,
    prepareHeaders: (headers, { endpoint }) => {
      // For the token endpoint, add Basic auth
      if (endpoint === 'getAccessToken') {
        headers.set('Authorization', `Basic ${config.api.apiToken}`);
        headers.set('Content-Type', 'application/x-www-form-urlencoded');
      } else {
        // For other endpoints, add Bearer token if available
        const token = localStorage.getItem('access_token');
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      }

      // Attach reese84 token from localStorage
      const reese84 = localStorage.getItem('reese84');
      if (reese84) {
        try {
          const parsed = JSON.parse(reese84);
          if (parsed?.token) {
            headers.set('x-d-token', parsed.token);
          }
        } catch (error) {
          console.error('Error parsing reese84 token:', error);
        }
      }

      // Attach preprod access header only when configured
      if (config.api.preprodAccessId) {
        headers.set('X-Hertz-Preprod-Access', config.api.preprodAccessId);
      }

      return headers;
    }
  });

// Shared in-flight refresh so concurrent requests don't each request a new token.
let refreshPromise: Promise<boolean> | null = null;

const refreshAccessToken = (
  api: Parameters<typeof rawBaseQuery>[1],
  extraOptions: Parameters<typeof rawBaseQuery>[2],
): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const result = await rawBaseQuery(
        {
          url: '/api/login/token',
          method: 'POST',
          body: new URLSearchParams({ 'grant_type': 'client_credentials' }),
        },
        { ...api, endpoint: 'getAccessToken' },
        extraOptions,
      );
      const data = result.data as TokenResponse | undefined;
      if (data?.access_token) {
        storeAccessToken(data.access_token, data.expires_in);
        return true;
      }
      console.error('Access token refresh failed:', result.error);
      return false;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const isTokenRequest = api.endpoint === 'getAccessToken';

  if (!isTokenRequest && isAccessTokenExpired()) {
    await refreshAccessToken(api, extraOptions);
  }

  let result = await rawBaseQuery(args, api, extraOptions);

  // A 401 without CORS headers reaches the client as FETCH_ERROR, not as a 401.
  if (!isTokenRequest && (isJwtExpiredResponse(result) || result.error?.status === 'FETCH_ERROR')) {
    if (await refreshAccessToken(api, extraOptions)) {
      result = await rawBaseQuery(args, api, extraOptions);
    }
    // Still expired after a refresh: surface it as an error instead of a success.
    if (isJwtExpiredResponse(result) && !result.error) {
      return { error: { status: 401, data: result.data } };
    }
  }

  return result;
};

// Create the API service
export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    updatePaymentRequest: builder.mutation<UpdatePaymentRequestResponse | null, { id: string; status: UpdatePaymentRequestRequest }>({
      query: ({ id, status }) => ({
        url: `/payments/payment-request/${id}`,
        method: 'PATCH',
        body: status,
        // This endpoint can answer with a plain-text body (e.g. "Pending
        // Transaction Completed: ..."). The default JSON handler would fail to
        // parse that and surface a PARSING_ERROR even though the call succeeded,
        // so fall back to wrapping the raw text as a message.
        responseHandler: async (response: Response) => {
          const text = await response.text();
          if (!text) {
            return null;
          }
          try {
            return JSON.parse(text);
          } catch {
            return { message: text };
          }
        },
      }),
    }),
    getAccessToken: builder.mutation<TokenResponse, void>({
      query: () => ({
        url: '/api/login/token',
        method: 'POST',
        body: new URLSearchParams({
          'grant_type': 'client_credentials'
        })
      }),
      transformResponse: (response: TokenResponse) => {
        storeAccessToken(response.access_token, response.expires_in);
        return response;
      }
    }),
    getPaymentRequest: builder.query<PaymentRequestResponse, string>({
      query: (paymentRequestId) => ({
        url: `/payments/payment-request/${paymentRequestId}`,
        method: 'GET'
      })
    })
  })
});

export const {
  useGetAccessTokenMutation,
  useGetPaymentRequestQuery,
  useUpdatePaymentRequestMutation
} = paymentApi;
